import { Navigate, useLocation } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useAuth } from '@/auth/context/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredGroups?: string[]
}

interface PublicRouteProps {
  children: React.ReactNode
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-black" />
        <p className="text-muted-foreground">Verificando sesión...</p>
      </div>
    </div>
  )
}

export function ProtectedRoute({ children, requiredGroups }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading state while checking auth
  if (isLoading) {
    return <LoadingScreen />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check required groups if specified
  if (requiredGroups && requiredGroups.length > 0 && user) {
    const hasRequiredGroup = requiredGroups.some((group) =>
      user.groups.includes(group)
    )

    if (!hasRequiredGroup && !user.is_superuser) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
            <p className="mt-2 text-muted-foreground">
              No tienes permisos para acceder a esta sección.
            </p>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

interface RequireGroupsProps {
  children: React.ReactNode
  groups: string[]
}

export function RequireGroups({ children, groups }: RequireGroupsProps) {
  const { user } = useAuth()

  if (!user) return null

  const hasAccess = user.is_superuser || groups.some((g) => user.groups.includes(g))

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
          <p className="mt-2 text-muted-foreground">
            No tienes permisos para acceder a esta sección.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

interface RequireAreasProps {
  children: React.ReactNode
  areas: string[]
}

export function RequireAreas({ children, areas }: RequireAreasProps) {
  const { user } = useAuth()

  if (!user) return null

  const normalizedAreas = areas.map((a) => a.toLowerCase())
  const hasAccess =
    user.is_superuser ||
    user.groups.includes('admin') ||
    user.areas.some((a) => normalizedAreas.includes(a.nombre.toLowerCase()))

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
          <p className="mt-2 text-muted-foreground">
            No tienes permisos para acceder a esta sección.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()

  // Show loading state while checking auth
  if (isLoading) {
    return <LoadingScreen />
  }

  // If authenticated, always redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
